// src/settings/GroupMemberModal.js - Supabase対応版
import React, { useState, useEffect } from "react";

function GroupMemberModal({ groupName, groupId, members, allStaff, onClose, onUpdate }) {
  const [editMode, setEditMode] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [newMemberId, setNewMemberId] = useState("");
  const [saving, setSaving] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");

  // モーダルの初期化時にメンバーを設定
  // ⚠️ skill_groups.members は employee_id ベースで保存されているため、
  //    ここでも employee_id を扱う（過去 m.id = UUID を使っていてメンバーが全消失するバグがあった）
  useEffect(() => {
    setSelectedMembers(members.map(m => m.employee_id));
  }, [members]);

  const removeMember = (employeeId) => {
    setSelectedMembers(prev => prev.filter(memberId => memberId !== employeeId));
  };

  const addMember = () => {
    if (newMemberId && !selectedMembers.includes(newMemberId)) {
      setSelectedMembers(prev => [...prev, newMemberId]);
      setNewMemberId("");
    }
  };

  const handleSave = async () => {
    if (!groupId) {
      alert("グループIDが見つかりません");
      return;
    }
    
    setSaving(true);
    try {
      // 親コンポーネント（SkillGroupSettings）の onUpdate を呼び出す
      // これが内部で manageSkillGroups('update', ...) を呼ぶ
      if (onUpdate) {
        await onUpdate(groupId, selectedMembers);
      }
      
      // 編集モードを終了
      setEditMode(false);
      setSaving(false);
    } catch (error) {
      console.error("グループメンバーの更新に失敗しました:", error);
      alert("グループメンバーの更新に失敗しました: " + error.message);
      setSaving(false);
    }
  };

  // メンバー候補のフィルタリング（すでに選択済みのメンバーを除外）
  const filteredStaff = allStaff
  .filter(s => !selectedMembers.includes(s.employee_id))
  .filter(s => {
    if (!searchKeyword) return true;
    const searchLower = searchKeyword.toLowerCase();
    return s.employee_id.toLowerCase().includes(searchLower) ||  // ← 検索用なのでemployee_idのまま
           `${s.last_name}${s.first_name}`.toLowerCase().includes(searchLower);
  });

  // スタイル定義
  const styles = {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0,0,0,0.7)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1000,
    },
    modalContainer: {
      backgroundColor: "white",
      borderRadius: "10px",
      boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
      width: "90%",
      maxWidth: "700px",
      maxHeight: "85vh",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
    },
    header: {
      borderBottom: "1px solid #eee",
      padding: "1.25rem 1.5rem",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    title: {
      margin: 0,
      fontSize: "1.3rem",
      fontWeight: "600",
      color: "#333",
    },
    closeButton: {
      background: "none",
      border: "none",
      fontSize: "1.5rem",
      color: "#666",
      cursor: "pointer",
    },
    body: {
      padding: "1.5rem",
      overflowY: "auto",
      flex: 1,
    },
    emptyMessage: {
      textAlign: "center",
      color: "#666",
      padding: "2rem 0",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      marginBottom: "1.5rem",
    },
    th: {
      textAlign: "left",
      padding: "0.75rem",
      borderBottom: "2px solid #ddd",
      color: "#444",
    },
    tr: {
      borderBottom: "1px solid #eee",
    },
    td: {
      padding: "0.75rem",
    },
    footer: {
      borderTop: "1px solid #eee",
      padding: "1rem 1.5rem",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      backgroundColor: "#f9f9f9",
    },
    editButton: {
      backgroundColor: "#6c757d",
      color: "white",
      border: "none",
      borderRadius: "4px",
      padding: "0.5rem 1rem",
      cursor: "pointer",
    },
    saveButton: {
      backgroundColor: "#007bff",
      color: "white",
      border: "none",
      borderRadius: "4px",
      padding: "0.6rem 1.2rem",
      cursor: "pointer",
      fontWeight: "500",
    },
    cancelButton: {
      backgroundColor: "#6c757d",
      color: "white",
      border: "none",
      borderRadius: "4px",
      padding: "0.6rem 1.2rem",
      marginRight: "0.75rem",
      cursor: "pointer",
    },
    deleteButton: {
      backgroundColor: "#dc3545",
      color: "white",
      border: "none",
      borderRadius: "4px",
      padding: "0.4rem 0.6rem",
      fontSize: "0.85rem",
      cursor: "pointer",
    },
    addSection: {
      marginTop: "1.5rem",
      padding: "1.25rem",
      backgroundColor: "#f8f9fa",
      borderRadius: "8px",
      border: "1px solid #e0e0e0",
    },
    addTitle: {
      margin: "0 0 1rem 0",
      fontSize: "1.1rem",
      fontWeight: "600",
      color: "#333",
    },
    addForm: {
      display: "flex",
      gap: "0.75rem",
      alignItems: "center",
      marginBottom: "1rem",
    },
    select: {
      flex: 1,
      padding: "0.6rem",
      borderRadius: "4px",
      border: "1px solid #ccc",
    },
    addButton: {
      backgroundColor: "#28a745",
      color: "white",
      border: "none",
      borderRadius: "4px",
      padding: "0.6rem 1rem",
      cursor: "pointer",
    },
    searchInput: {
      padding: "0.6rem",
      borderRadius: "4px",
      border: "1px solid #ccc",
      marginBottom: "1rem",
      width: "100%",
    },
    disabledButton: {
      opacity: 0.65,
      cursor: "not-allowed",
    },
    badge: {
      display: "inline-block",
      padding: "0.25rem 0.5rem",
      borderRadius: "20px",
      fontSize: "0.85rem",
      fontWeight: "500",
      color: "white",
      backgroundColor: "#007bff",
      marginLeft: "0.5rem",
    },
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>
            {groupName} のメンバー一覧
            <span style={styles.badge}>{selectedMembers.length}名</span>
          </h3>
          <button style={styles.closeButton} onClick={onClose}>×</button>
        </div>

        <div style={styles.body}>
          {selectedMembers.length === 0 ? (
            <div style={styles.emptyMessage}>該当するメンバーがいません。</div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>社員番号</th>
                  <th style={styles.th}>氏名</th>
                  <th style={styles.th}>入職年</th>
                  {editMode && <th style={styles.th}>操作</th>}
                </tr>
              </thead>
              <tbody>
              {allStaff
  .filter(s => selectedMembers.includes(s.employee_id))
  .map((m) => (
    <tr key={m.employee_id} style={styles.tr}>
      <td style={styles.td}>{m.employee_id}</td>
      <td style={styles.td}>
        {m.last_name} {m.first_name}
      </td>
      <td style={styles.td}>{m.year}</td>
      {editMode && (
        <td style={styles.td}>
          <button
            onClick={() => removeMember(m.employee_id)}
            style={styles.deleteButton}
          >
            削除
          </button>
        </td>
      )}
    </tr>
  ))}
              </tbody>
            </table>
          )}

          {/* メンバー追加フォーム (編集モード時のみ表示) */}
          {editMode && (
            <div style={styles.addSection}>
              <h4 style={styles.addTitle}>メンバーを追加</h4>
              
              <input
                type="text"
                placeholder="社員番号または氏名で検索"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                style={styles.searchInput}
              />
              
              <div style={styles.addForm}>
                <select
                  value={newMemberId}
                  onChange={(e) => setNewMemberId(e.target.value)}
                  style={styles.select}
                >
                  <option value="">-- スタッフを選択 --</option>
                  {filteredStaff.map((s) => (
                    <option key={s.employee_id} value={s.employee_id}>
                    {s.employee_id}: {s.last_name} {s.first_name}
                  </option>
                  ))}
                </select>
                <button
                  onClick={addMember}
                  disabled={!newMemberId}
                  style={{
                    ...styles.addButton,
                    ...((!newMemberId) ? styles.disabledButton : {})
                  }}
                >
                  追加
                </button>
              </div>
              
              <div style={{ fontSize: "0.9rem", color: "#666" }}>
                ※ メンバーを追加すると、条件設定とは無関係に手動でグループにメンバーを追加できます
              </div>
            </div>
          )}
        </div>

        <div style={styles.footer}>
          {!editMode ? (
            <>
              <button
                onClick={() => setEditMode(true)}
                style={styles.editButton}
              >
                メンバー編集
              </button>
              <button onClick={onClose} style={styles.saveButton}>
                閉じる
              </button>
            </>
          ) : (
            <>
              <div>
                <button
                  onClick={() => {
                    // キャンセル時は元のメンバーリストに戻す（employee_id ベース）
                    setSelectedMembers(members.map(m => m.employee_id));
                    setEditMode(false);
                  }}
                  style={styles.cancelButton}
                >
                  キャンセル
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    ...styles.saveButton,
                    ...(saving ? styles.disabledButton : {})
                  }}
                >
                  {saving ? "保存中..." : "変更を保存"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default GroupMemberModal;